import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Box, Drawer, AppBar, Toolbar, Typography,
  List, ListItem, ListItemButton, ListItemIcon,
  ListItemText, IconButton, Divider, Tooltip,
  Badge, Menu, MenuItem, Button,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import BusinessIcon from '@mui/icons-material/Business';
import FolderIcon from '@mui/icons-material/Folder';
import BarChartIcon from '@mui/icons-material/BarChart';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonIcon from '@mui/icons-material/Person';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import CampaignIcon from '@mui/icons-material/Campaign';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import SpeedIcon from '@mui/icons-material/Speed';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import LogoutIcon from '@mui/icons-material/Logout';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CakeIcon from '@mui/icons-material/Cake';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import api from '../services/api';
import { isImpersonando, getPersonificandoNome, encerrarPersonificacao } from '../services/authStorage';
import { VERSION } from '../version';

const DRAWER_WIDTH = 240;

const STATUS_COR = {
  disponivel: '#16A34A',
  em_campo:   '#2563EB',
  em_reuniao: '#D97706',
  ferias:     '#7C3AED',
  afastado:   '#94A3B8',
};

const PERFIL_INFO = {
  diretor:    { label: 'Diretor' },
  gestor:     { label: 'Gestor' },
  consultor:  { label: 'Consultor' },
  financeiro: { label: 'Financeiro' },
  comercial:  { label: 'Comercial' },
  marketing:  { label: 'Marketing' },
  rh:         { label: 'RH' },
};

export default function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, isSuperAdmin, perfil } = useAuth();

  const isImpersonating = isImpersonando();
  const personificandoNome = getPersonificandoNome();

  const handleVoltarAdmin = () => {
    if (encerrarPersonificacao()) {
      window.location.href = '/login-como';
    }
  };
  // `todasNotif` guarda as últimas notificações (lidas e não lidas); `notificacoes` são só as novas.
  const [todasNotif, setTodasNotif] = useState([]);
  const [verAnteriores, setVerAnteriores] = useState(false);
  const notificacoes = todasNotif.filter((n) => !n.lida);
  const anteriores = todasNotif.filter((n) => n.lida).slice(0, 20);
  const [anchorEl, setAnchorEl] = useState(null);
  const [aniversariantes, setAniversariantes] = useState([]);
  const [anchorElAniversario, setAnchorElAniversario] = useState(null);

  const menuItems = [
    { text: 'Taxa de Ocupação',  icon: <BarChartIcon />,             path: '/',                chave: 'dashboard',       visivel: true,          grupo: 'Operação' },
    { text: 'Controle de Carga', icon: <SpeedIcon />,                path: '/controle-carga',  chave: 'controle_carga',  visivel: true,          grupo: 'Operação' },
    { text: 'Agenda',            icon: <CalendarMonthIcon />,        path: '/agenda',          chave: 'agenda',          visivel: true,          grupo: 'Operação' },
    { text: 'Células',           icon: <AccountTreeOutlinedIcon />,  path: '/celulas',         chave: 'celulas',         visivel: true,          grupo: 'Operação' },
    { text: 'Projetos',          icon: <FolderIcon />,               path: '/projetos',        chave: 'projetos',        visivel: true,          grupo: 'Operação' },
    { text: 'Equipe',             icon: <PeopleIcon />,               path: '/membros-equipe',  chave: 'membros_equipe',  visivel: true,          grupo: 'Operação' },
    { text: 'Clientes',          icon: <BusinessIcon />,             path: '/clientes',        chave: 'clientes',        visivel: true,          grupo: 'Operação' },
    { text: 'Relatórios',        icon: <AssessmentIcon />,           path: '/relatorios',      chave: 'relatorios',      visivel: true,          grupo: 'Operação' },
    { text: 'Financeiro',        icon: <AttachMoneyIcon />,          path: '/financeiro',      chave: 'financeiro',      visivel: true,          grupo: 'Negócio' },
    { text: 'Comercial',         icon: <BusinessCenterIcon />,       path: '/comercial',       chave: 'comercial',       visivel: true,          grupo: 'Negócio' },
    { text: 'CRM',                icon: <AccountTreeIcon />,          path: '/crm',             chave: 'crm',             visivel: true,          grupo: 'Negócio' },
    { text: 'Marketing',         icon: <CampaignIcon />,             path: '/marketing',       chave: 'marketing',       visivel: true,          grupo: 'Negócio' },
    { text: 'RH',                 icon: <PeopleAltIcon />,            path: '/rh',              chave: 'rh',              visivel: true,          grupo: 'Negócio' },
    { text: 'Meu perfil',        icon: <PersonIcon />,               path: '/perfil',          chave: 'perfil',          visivel: true,          grupo: 'Conta' },
    { text: 'Login como',        icon: <SwapHorizIcon />,            path: '/login-como',      chave: 'dashboard',       visivel: isSuperAdmin,  grupo: 'Conta' },
  ];

  const grupos = ['Operação', 'Negócio', 'Conta'];

  const carregarNotificacoes = () => {
    api.get('/notificacoes/').then((res) => {
      setTodasNotif(Array.isArray(res.data) ? res.data : (res.data.results || []));
    }).catch(() => {});
  };

  useEffect(() => {
    carregarNotificacoes();
    const interval = setInterval(carregarNotificacoes, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    api.get('/fichas-cadastrais/aniversariantes/').then((res) => {
      setAniversariantes(res.data);
    }).catch(() => {});
  }, []);

  const handleAbrirNotif = (e) => {
    setAnchorEl(e.currentTarget);
    setVerAnteriores(false);
    carregarNotificacoes();   // atualiza na hora de abrir, sem esperar os 30 s
  };
  const handleFecharNotif = () => setAnchorEl(null);

  const handleAbrirAniversario = (e) => setAnchorElAniversario(e.currentTarget);
  const handleFecharAniversario = () => setAnchorElAniversario(null);

  // Clicar numa notificação: marca como lida e abre a tela do assunto (quando ela tem link).
  const handleAbrirNotificacao = async (n) => {
    handleFecharNotif();
    if (!n.lida) {
      setTodasNotif((lista) => lista.map((x) => (x.id === n.id ? { ...x, lida: true } : x)));
      try {
        await api.patch(`/notificacoes/${n.id}/`, { lida: true });
      } catch (e) {
        carregarNotificacoes();   // não conseguiu salvar: volta ao estado do servidor
      }
    }
    if (n.link) navigate(n.link);
  };

  const handleMarcarTodasLidas = async () => {
    setTodasNotif((lista) => lista.map((x) => ({ ...x, lida: true })));
    try {
      await api.post('/notificacoes/marcar-todas-lidas/');
    } catch (e) {
      carregarNotificacoes();
    }
  };

  const quandoFoi = (iso) => {
    const d = new Date(iso);
    const dias = Math.floor((Date.now() - d.getTime()) / 86400000);
    if (dias <= 0) return 'hoje';
    if (dias === 1) return 'ontem';
    if (dias < 7) return `há ${dias} dias`;
    return d.toLocaleDateString('pt-BR');
  };

  const statusCor = STATUS_COR[perfil?.status] || '#94A3B8';
  const perfilLabel = perfil?.cargo || PERFIL_INFO[perfil?.perfil]?.label || perfil?.perfil;
  const aniversariantesHoje = aniversariantes.filter((a) => a.hoje).length;
  const nomeMesAtual = new Date().toLocaleDateString('pt-BR', { month: 'long' });

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: `calc(100% - ${DRAWER_WIDTH}px)`,
          ml: `${DRAWER_WIDTH}px`,
          backgroundColor: '#13375F',
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        }}
      >
        <Toolbar>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontSize: 17 }}>
            Sistema de Gestão
          </Typography>
          {isImpersonating && (
            <Button
              variant="contained"
              size="small"
              color="error"
              onClick={handleVoltarAdmin}
              sx={{ mr: 1.5, fontSize: 11 }}
            >
              ⚠️ Saindo como {personificandoNome} — Clique para voltar
            </Button>
          )}

          <Tooltip title="Aniversariantes do mês">
            <IconButton color="inherit" onClick={handleAbrirAniversario}>
              <Badge badgeContent={aniversariantesHoje} color="error">
                <CakeIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          <Menu
  anchorEl={anchorElAniversario}
  open={Boolean(anchorElAniversario)}
  onClose={handleFecharAniversario}
  PaperProps={{ sx: { width: 340, maxHeight: 480, borderRadius: 2, overflow: 'hidden' } }}
>
  <Box sx={{
    background: 'linear-gradient(135deg, #1a1a2e 0%, #e94560 130%)',
    color: 'white', px: 2.5, py: 2,
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <CakeIcon fontSize="small" />
      <Typography variant="subtitle1" fontWeight={700} sx={{ textTransform: 'capitalize' }}>
        Aniversariantes de {nomeMesAtual}
      </Typography>
    </Box>
  </Box>

  {aniversariantesHoje > 0 && (
    <Box sx={{ backgroundColor: '#FAEEDA', borderBottom: '1px solid #f0e0c0', px: 2.5, py: 1.5 }}>
      <Typography sx={{ fontWeight: 700, color: '#BA7517', fontSize: 14, textAlign: 'center' }}>
        🎉🎂 Feliz Aniversário{aniversariantesHoje === 1 ? '' : 's'}!!! 🎂🎉
      </Typography>
      <Typography sx={{ fontSize: 12.5, color: '#8a5a10', textAlign: 'center', mt: 0.3 }}>
        {aniversariantes.filter((a) => a.hoje).map((a) => a.nome).join(', ')}
      </Typography>
    </Box>
  )}

  <Box sx={{ maxHeight: 320, overflowY: 'auto' }}>
    {aniversariantes.length === 0 ? (
      <Box sx={{ px: 2.5, py: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Ninguém faz aniversário este mês
        </Typography>
      </Box>
    ) : (
      aniversariantes.map((a) => (
        <Box
          key={a.id}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            px: 2.5, py: 1.2,
            borderBottom: '1px solid #f0f0f0',
            backgroundColor: a.hoje ? '#FCEBEB' : 'transparent',
          }}
        >
          <Box sx={{
            width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
            backgroundColor: a.hoje ? '#e94560' : '#1a1a2e',
            color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, fontWeight: 700,
          }}>
            {a.nome.charAt(0).toUpperCase()}
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={a.hoje ? 700 : 500} noWrap>
              {a.nome}
            </Typography>
            {a.hoje && (
              <Typography variant="caption" sx={{ color: '#e94560', fontWeight: 600 }}>
                🎂 Hoje!
              </Typography>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', flexShrink: 0 }}>
            {String(a.dia).padStart(2, '0')}/{String(a.mes).padStart(2, '0')}
          </Typography>
        </Box>
      ))
    )}
  </Box>
</Menu>

          <Tooltip title="Notificações">
            <IconButton color="inherit" onClick={handleAbrirNotif}>
              <Badge badgeContent={notificacoes.length} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          <Menu
  anchorEl={anchorEl}
  open={Boolean(anchorEl)}
  onClose={handleFecharNotif}
  PaperProps={{ sx: { width: 360, maxHeight: 480, borderRadius: 2, overflow: 'hidden' } }}
>
  <Box sx={{
    background: 'linear-gradient(135deg, #1a1a2e 0%, #e94560 130%)',
    color: 'white', px: 2.5, py: 2,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <NotificationsIcon fontSize="small" />
      <Typography variant="subtitle1" fontWeight={700}>
        Notificações
      </Typography>
    </Box>
    {!verAnteriores && notificacoes.length > 0 && (
      <Typography
        onClick={handleMarcarTodasLidas}
        sx={{ fontSize: 12, cursor: 'pointer', opacity: 0.85, textDecoration: 'underline', '&:hover': { opacity: 1 } }}
      >
        Marcar todas como lidas
      </Typography>
    )}
  </Box>

  <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
    {(verAnteriores ? anteriores : notificacoes).length === 0 ? (
      <Box sx={{ px: 2.5, py: 4, textAlign: 'center' }}>
        <NotificationsIcon sx={{ fontSize: 32, color: '#d0d0d0', mb: 0.5 }} />
        <Typography variant="body2" color="text.secondary">
          {verAnteriores ? 'Nenhuma notificação anterior' : 'Nenhuma notificação nova'}
        </Typography>
      </Box>
    ) : (
      (verAnteriores ? anteriores : notificacoes).map((n) => (
        <Box
          key={n.id}
          onClick={() => handleAbrirNotificacao(n)}
          sx={{
            display: 'flex', gap: 1.5,
            px: 2.5, py: 1.5,
            borderBottom: '1px solid #f0f0f0',
            cursor: 'pointer',
            opacity: n.lida ? 0.65 : 1,
            '&:hover': { backgroundColor: '#FCEBEB' },
          }}
        >
          <Box sx={{
            width: 8, height: 8, borderRadius: '50%', flexShrink: 0, mt: 0.7,
            backgroundColor: n.lida ? '#cbd5e1' : (n.nivel >= 2 ? '#E24B4A' : '#BA7517'),
          }} />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={n.lida ? 500 : 700} color="#1a1a2e">
              {n.titulo}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.2 }}>
              {n.mensagem}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.4, color: '#94a3b8' }}>
              {quandoFoi(n.criado_em)}{n.link ? ' · clique para abrir' : ''}
            </Typography>
          </Box>
        </Box>
      ))
    )}
  </Box>

  <Box
    onClick={() => setVerAnteriores((v) => !v)}
    sx={{
      px: 2.5, py: 1.2, textAlign: 'center', cursor: 'pointer',
      borderTop: '1px solid #f0f0f0', backgroundColor: '#fafafa',
      fontSize: 12.5, fontWeight: 600, color: '#13375F',
      '&:hover': { backgroundColor: '#f1f5f9' },
    }}
  >
    {verAnteriores ? 'Voltar às novas' : 'Ver anteriores'}
  </Box>
</Menu>

          <Box
            onClick={() => navigate('/perfil')}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
              ml: 1.5, px: 1, py: 0.5, borderRadius: 2,
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
            }}
          >
            <Box sx={{
              width: 34, height: 34, borderRadius: '50%', p: '2px', flexShrink: 0,
              border: `2px solid ${statusCor}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Box sx={{
                width: '100%', height: '100%', borderRadius: '50%',
                backgroundColor: '#e94560',
                backgroundImage: perfil?.foto_url ? `url(${perfil.foto_url})` : 'none',
                backgroundSize: 'cover', backgroundPosition: 'center',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'white', fontSize: 13, fontWeight: 700,
              }}>
                {!perfil?.foto_url && perfil?.nome_completo?.charAt(0)}
              </Box>
            </Box>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Typography sx={{ color: 'white', fontSize: 13, fontWeight: 600, lineHeight: 1.25 }}>
                {perfil?.nome_completo || '—'}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, lineHeight: 1.25 }}>
                {perfilLabel || ''}
              </Typography>
            </Box>
          </Box>

          <Tooltip title="Sair do sistema">
            <IconButton color="inherit" onClick={logout} sx={{ ml: 1 }}>
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            background: 'linear-gradient(180deg, #13375F 0%, #0d2642 100%)',
            backgroundImage: `
              repeating-linear-gradient(45deg, rgba(255,255,255,0.025) 0px, rgba(255,255,255,0.025) 1px, transparent 1px, transparent 26px),
              linear-gradient(180deg, #13375F 0%, #0d2642 100%)
            `,
            color: 'white',
          },
        }}
      >
        <Toolbar sx={{ alignItems: 'flex-start', pt: 2.5 }}>
          <Box display="flex" flexDirection="column" width="100%">
            <Typography variant="subtitle1" fontWeight={700} color="white" lineHeight={1.4}>
              Lean Way Consulting
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, mt: 0.3, display: 'block' }}
            >
              v{VERSION}
            </Typography>
          </Box>
        </Toolbar>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

        <List sx={{ px: 1, py: 1 }}>
          {grupos.map((grupo) => {
            const itensDoGrupo = menuItems.filter((item) => item.grupo === grupo && item.visivel);
            if (itensDoGrupo.length === 0) return null;
            return (
              <Box key={grupo} sx={{ mb: 0.5 }}>
                <Typography sx={{
                  px: 1.5, pt: 1.5, pb: 0.5, fontSize: 10.5, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '0.08em',
                  color: 'rgba(255,255,255,0.35)',
                }}>
                  {grupo}
                </Typography>
                {itensDoGrupo.map((item) => (
                  <ListItem key={item.text} disablePadding sx={{ px: 0.5 }}>
                    <ListItemButton
                      onClick={() => navigate(item.path)}
                      selected={location.pathname === item.path || location.pathname.startsWith(item.path + '/')}
                      sx={{
                        borderRadius: 1.5, mb: 0.3,
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(233, 66, 94, 0.18)',
                          borderLeft: '3px solid #e94560',
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.06)',
                        },
                        color: 'rgb(255, 255, 255)',
                      }}
                    >
                      <ListItemIcon sx={{ color: 'rgb(255, 255, 255)', minWidth: 38 }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: 13.5 }} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </Box>
            );
          })}
        </List>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          mt: 8,
          backgroundColor: '#f8f9fa',
          minHeight: '100vh',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
